
import React, { useRef, useState } from "react";
import QRCodeStyling from "qr-code-styling";
import LogoUploader from "./LogoUploader";

const styleOptions = [
  { value: "dots", label: "Dots", dotsOptions: { type: "dots" } },
  { value: "rounded", label: "Rounded", dotsOptions: { type: "rounded" } },
  { value: "classy", label: "Classy", dotsOptions: { type: "classy" } },
  { value: "classy-rounded", label: "Classy Rounded", dotsOptions: { type: "classy-rounded" } },
  { value: "square", label: "Square", dotsOptions: { type: "square" } },
  { value: "extra-rounded", label: "Extra Rounded", dotsOptions: { type: "extra-rounded" } },
];

const cornerSquareOptions = [
  { value: "dot", label: "Dot", type: "dot" },
  { value: "square", label: "Square", type: "square" },
  { value: "extra-rounded", label: "Extra Rounded", type: "extra-rounded" },
];

const cornerDotOptions = [
  { value: "dot", label: "Dot", type: "dot" },
  { value: "square", label: "Square", type: "square" },
];

const themePresets = [
  { name: "Blue", dots: "#2563eb", bg: "#ffffff", cornersSquare: "#1e40af", cornersDot: "#1e40af" },
  { name: "Emerald", dots: "#059669", bg: "#ffffff", cornersSquare: "#064e3b", cornersDot: "#064e3b" },
  { name: "Purple", dots: "#7c3aed", bg: "#ffffff", cornersSquare: "#4c1d95", cornersDot: "#4c1d95" },
  { name: "Rose", dots: "#e11d48", bg: "#ffffff", cornersSquare: "#881337", cornersDot: "#881337" },
  { name: "Amber", dots: "#d97706", bg: "#ffffff", cornersSquare: "#78350f", cornersDot: "#78350f" },
];

const defaultTheme = themePresets[0];

const QRGenerator = () => {
  const [qrData, setQrData] = useState("https://google.com");
  const [logo, setLogo] = useState(null);
  const [dotsColor, setDotsColor] = useState(defaultTheme.dots);
  const [bgColor, setBgColor] = useState(defaultTheme.bg);
  const [cornersSquareColor, setCornersSquareColor] = useState(defaultTheme.cornersSquare);
  const [cornersDotColor, setCornersDotColor] = useState(defaultTheme.cornersDot);
  const [selectedStyle, setSelectedStyle] = useState(styleOptions[0].value);
  const [selectedCornerSquare, setSelectedCornerSquare] = useState(cornerSquareOptions[0].value);
  const [selectedCornerDot, setSelectedCornerDot] = useState(cornerDotOptions[0].value);

  const qrCodeRef = useRef(null);
  const qrInstance = useRef(null);

  const getStyleConfig = () => {
    const style = styleOptions.find((s) => s.value === selectedStyle);
    return style ? style.dotsOptions : styleOptions[0].dotsOptions;
  };

  const generateQRCode = () => {
    if (!qrData) return;

    if (qrCodeRef.current) qrCodeRef.current.innerHTML = "";

    qrInstance.current = new QRCodeStyling({
      width: 300,
      height: 300,
      type: "svg",
      data: qrData,
      image: logo,
      margin: 10,
      dotsOptions: {
        color: dotsColor,
        ...getStyleConfig(),
      },
      backgroundOptions: {
        color: bgColor,
      },
      cornersSquareOptions: {
        color: cornersSquareColor,
        type: selectedCornerSquare,
      },
      cornersDotOptions: {
        color: cornersDotColor,
        type: selectedCornerDot,
      },
      imageOptions: {
        crossOrigin: "anonymous",
        margin: 5,
        imageSize: 0.4,
      },
    });

    qrInstance.current.append(qrCodeRef.current);
  };

  React.useEffect(() => {
    generateQRCode();
  }, [qrData, logo, dotsColor, bgColor, cornersSquareColor, cornersDotColor, selectedStyle, selectedCornerSquare, selectedCornerDot]);

  const handleDownload = (ext = "png") => {
    if (qrInstance.current) {
      qrInstance.current.download({ name: "qr-code", extension: ext });
    }
  };

  const handleThemeChange = (theme) => {
    setDotsColor(theme.dots);
    setBgColor(theme.bg);
    setCornersSquareColor(theme.cornersSquare);
    setCornersDotColor(theme.cornersDot);
  };

  return (
    <div className="w-full max-w-6xl mx-auto flex flex-col lg:flex-row gap-6 lg:gap-8 items-start">
      {/* Mobile Preview - shown on small screens at the top */}
      <div className="lg:hidden w-full animate-scale-in">
        <PreviewPanel
          qrCodeRef={qrCodeRef}
          handleDownload={handleDownload}
        />
      </div>

      {/* Left Column: Controls */}
      <div className="flex-1 w-full space-y-5 sm:space-y-6">
        {/* Data Input */}
        <section className="glass-card p-5 sm:p-6 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <SectionHeader number="1" color="blue" title="QR Content" />
          <div className="space-y-4">
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-slate-500 mb-2">URL or Text</label>
              <input
                type="text"
                value={qrData}
                onChange={e => setQrData(e.target.value)}
                placeholder="https://example.com"
                className="w-full glass-input px-4 py-3 sm:py-3.5 text-base sm:text-lg text-slate-800 font-medium"
              />
            </div>
            <LogoUploader onLogoChange={setLogo} />
          </div>
        </section>

        {/* Styles & Customization */}
        <section className="glass-card p-5 sm:p-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          <SectionHeader number="2" color="emerald" title="Design & Style" />

          <div className="space-y-6 sm:space-y-8">
            {/* Pattern Style */}
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-slate-500 mb-3">Pattern Style</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                {styleOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSelectedStyle(opt.value)}
                    className={`style-pill ${selectedStyle === opt.value ? 'active' : ''}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Corner Styles */}
            <div className="grid sm:grid-cols-2 gap-5 sm:gap-6">
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-slate-500 mb-3">Eye Frame</label>
                <div className="flex flex-wrap gap-2">
                  {cornerSquareOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setSelectedCornerSquare(opt.value)}
                      className={`corner-pill ${selectedCornerSquare === opt.value ? 'active' : ''}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-slate-500 mb-3">Eye Ball</label>
                <div className="flex flex-wrap gap-2">
                  {cornerDotOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setSelectedCornerDot(opt.value)}
                      className={`corner-pill ${selectedCornerDot === opt.value ? 'active' : ''}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Colors */}
        <section className="glass-card p-5 sm:p-6 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
          <SectionHeader number="3" color="amber" title="Colors" />

          <div className="space-y-6">
            {/* Quick Themes */}
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-slate-500 mb-3">Theme Presets</label>
              <div className="flex flex-wrap gap-2 sm:gap-3">
                {themePresets.map((theme) => (
                  <button
                    key={theme.name}
                    onClick={() => handleThemeChange(theme)}
                    className="theme-btn"
                  >
                    <div className="flex -space-x-1">
                      <div className="w-4 h-4 rounded-full border-2 border-white shadow-sm" style={{ background: theme.dots }} />
                      <div className="w-4 h-4 rounded-full border-2 border-white shadow-sm" style={{ background: theme.cornersSquare }} />
                    </div>
                    <span className="text-xs font-bold text-slate-600">{theme.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              <ColorInput label="Pattern" value={dotsColor} onChange={setDotsColor} />
              <ColorInput label="Background" value={bgColor} onChange={setBgColor} />
              <ColorInput label="Frame" value={cornersSquareColor} onChange={setCornersSquareColor} />
              <ColorInput label="Ball" value={cornersDotColor} onChange={setCornersDotColor} />
            </div>
          </div>
        </section>
      </div>

      {/* Right Column: Preview (Desktop - Sticky) */}
      <div className="hidden lg:block lg:w-[380px] xl:w-[400px] preview-sticky">
        <PreviewPanel
          qrCodeRef={qrCodeRef}
          handleDownload={handleDownload}
        />
      </div>
    </div>
  );
};

/* Section Header subcomponent */
const SectionHeader = ({ number, color, title }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
  };

  return (
    <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-5 flex items-center gap-2.5">
      <span className={`section-badge ${colorClasses[color]}`}>{number}</span>
      {title}
    </h2>
  );
};

/* Preview Panel subcomponent */
const PreviewPanel = ({ qrCodeRef, handleDownload }) => (
  <div className="flex flex-col items-center w-full">
    <div className="preview-card p-6 sm:p-8 w-full flex flex-col items-center">
      <div className="qr-display p-4 sm:p-5 relative group">
        <div ref={qrCodeRef} className="flex justify-center" />
        <div className="absolute inset-0 bg-blue-600/0 group-hover:bg-blue-600/5 transition-colors pointer-events-none rounded-[1rem]" />
      </div>

      <div className="mt-6 sm:mt-8 w-full space-y-3">
        <button
          onClick={() => handleDownload('png')}
          className="w-full btn-primary flex items-center justify-center gap-2 py-3.5"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Download PNG
        </button>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => handleDownload('svg')}
            className="btn-secondary py-2.5 text-sm"
          >
            SVG
          </button>
          <button
            onClick={() => handleDownload('jpeg')}
            className="btn-secondary py-2.5 text-sm"
          >
            JPG
          </button>
        </div>
      </div>

      <p className="mt-5 text-slate-400 text-xs text-center font-medium">
        High resolution, print-ready output
      </p>
    </div>

    {/* Pro Tip */}
    <div className="mt-5 sm:mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 w-full animate-slide-in" style={{ borderRadius: 'var(--radius-card)' }}>
      <p className="text-blue-700 text-xs sm:text-sm leading-relaxed">
        <span className="font-bold mr-1">Pro Tip:</span>
        Add a logo to make your QR code look more professional and trustworthy.
      </p>
    </div>
  </div>
);

const ColorInput = ({ label, value, onChange }) => (
  <div className="space-y-1.5">
    <label className="text-[10px] sm:text-[11px] uppercase font-bold text-slate-400 tracking-wider block ml-0.5">{label}</label>
    <div className="relative h-11 w-full color-picker-wrap cursor-pointer">
      <input
        type="color"
        value={value}
        onChange={e => onChange(e.target.value)}
        className="absolute inset-[-4px] w-[calc(100%+8px)] h-[calc(100%+8px)] cursor-pointer"
      />
    </div>
  </div>
);

export default QRGenerator;
